import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faEye,
  faEyeSlash,
  faGear,
  faPaperPlane,
  faPowerOff,
  faRotate,
  faToggleOff,
  faToggleOn,
} from '@fortawesome/free-solid-svg-icons';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { WebhookDialogComponent } from './webhook-dialog/webhook-dialog.component';
import { WebhooksService } from './webhooks.service';
import { ButtonComponent } from '@akostm/orbit-ui';

@Component({
  selector: 'lib-settings-webhooks',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FontAwesomeModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './webhooks.component.html',
  styleUrls: ['./webhooks.component.scss'],
})
export class WebhooksComponent implements OnInit {
  // Service injections
  readonly #webhooksService = inject(WebhooksService);
  readonly #dialog = inject(Dialog);
  readonly #translateService = inject(TranslateService);
  readonly #fb = inject(FormBuilder);

  // Font Awesome icons
  readonly faRotate = faRotate;
  readonly faGear = faGear;
  readonly faPaperPlane = faPaperPlane;
  readonly faEye = faEye;
  readonly faEyeSlash = faEyeSlash;
  readonly faPowerOff = faPowerOff;
  readonly faToggleOn = faToggleOn;
  readonly faToggleOff = faToggleOff;

  readonly loading = this.#webhooksService.loading;
  readonly rotatingSecret = this.#webhooksService.rotatingSecret;
  readonly testingWebhook = this.#webhooksService.testingWebhook;
  readonly updatingEndpoint = this.#webhooksService.updatingEndpoint;
  readonly webhookEnabled = this.#webhooksService.webhookEnabled;
  readonly endpointUrl = this.#webhooksService.endpointUrl;
  readonly secret = this.#webhooksService.secret;
  readonly secretVisible = this.#webhooksService.secretVisible;
  readonly testingSent = this.#webhooksService.testingSent;

  // Webhook features
  readonly availableWebhookTypes = this.#webhooksService.availableWebhookTypes;
  readonly loadingWebhookFeatures = this.#webhooksService.loadingWebhookFeatures;
  readonly webhookFeatures = this.#webhooksService.webhookFeatures;
  readonly updatingWebhookFeature = this.#webhooksService.updatingWebhookFeature;

  ngOnInit(): void {
    this.#webhooksService.loadWebhookSettings();
    this.#webhooksService.loadWebhookFeatures();
  }

  openWebhookDialog(): void {
    const endpointUrlValue = this.endpointUrl();

    const webhookForm = this.#fb.group({
      endpointUrl: [endpointUrlValue || '', [Validators.pattern('https?://.*')]],
    });

    const dialogRef = this.#dialog.open(WebhookDialogComponent, {
      data: {
        form: webhookForm,
        isUpdate: !!endpointUrlValue,
        title: this.#translateService.instant(
          endpointUrlValue ? 'SETTINGS.WEBHOOKS.UPDATE_WEBHOOK' : 'SETTINGS.WEBHOOKS.ADD_WEBHOOK'
        ),
        cancelLabel: this.#translateService.instant('SETTINGS.WEBHOOKS.CANCEL'),
        confirmLabel: this.#translateService.instant(
          endpointUrlValue ? 'SETTINGS.WEBHOOKS.UPDATE' : 'SETTINGS.WEBHOOKS.SAVE'
        ),
      },
      width: '32rem',
    });

    dialogRef.closed.subscribe((result) => {
      if (result && webhookForm.valid) {
        this.#webhooksService.updateWebhookSettings(webhookForm.value.endpointUrl || null);
      }
    });
  }

  // Now this method only handles disabling webhooks
  toggleWebhookStatus(): void {
    this.#webhooksService.updateWebhookSettings(null);
  }

  rotateSecret(): void {
    this.#webhooksService.rotateSecret();
  }

  testWebhook(): void {
    this.#webhooksService.testWebhook();
  }

  toggleSecretVisibility(): void {
    this.#webhooksService.toggleSecretVisibility();
  }

  toggleWebhookFeature(webhookType: string, enabled: boolean): void {
    this.#webhooksService.updateWebhookFeature(webhookType, enabled);
  }

  isWebhookFeatureEnabled(webhookType: string): boolean {
    const feature = this.webhookFeatures().find((f) => f.webhookType === webhookType);
    return !!feature?.enabled;
  }

  getWebhookDescription(webhookType: string): string {
    // Map webhook types to their description translation keys
    const descriptionMap: Record<string, string> = {
      'conversation.created': 'SETTINGS.WEBHOOKS.CONVERSATION_CREATED_DESC',
      'conversation.message_added': 'SETTINGS.WEBHOOKS.MESSAGE_ADDED_DESC',
      'conversation.shared_user_participant_read_status_updated':
        'SETTINGS.WEBHOOKS.CONVERSATION_READ_STATUS_DESC',
    };

    const translationKey = descriptionMap[webhookType] || 'SETTINGS.WEBHOOKS.DEFAULT_DESC';
    return this.#translateService.instant(translationKey);
  }
}
